import { computed, inject, Inject, Injectable, signal } from '@angular/core';
import { DataService } from 'ag-common-svc/lib/services/data.service';
import { ChangeSourceType } from '@ag-common-lib/lib/lists/change-logs/change-source-type.enum';
import { FIREBASE_APP } from 'ag-common-svc/lib/injections/firebase-app';
import { FirebaseApp } from 'firebase/app';
import {
  updateWithAgentMetadata,
  userAgreementFromFirestore,
} from 'ag-common-svc/lib/services/user-agreements/user-agreements-service-utils';
import {
  CurrentUserAgreements,
  SupportedLanguages,
  UserAgreementManagement,
  UserAgreementManagementKeys,
  UserAgreementStatus,
} from '@ag-common-lib/lib/models/utils/user-agreement-management.model';
import { BehaviorSubject, distinctUntilChanged, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { DataSource } from 'devextreme/common/data';
import { map } from 'rxjs/operators';
import ArrayStore from 'devextreme/data/array_store';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { cloneDeep } from 'lodash';
import { UntilDestroy } from '@ngneat/until-destroy';
import { QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PolicyTypes } from '@ag-common-lib/public-api';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';

export const USER_AGREEMENTS_MANAGEMENT_FIRESTORE_COLLECTION = 'user-agreements-management';

const initialCurrentUserAgreements: CurrentUserAgreements = {
  [PolicyTypes.privacyPolicy]: null,
  [PolicyTypes.termsOfService]: null,
};

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class UserAgreementsManagementService extends DataService<UserAgreementManagement> {
  private readonly _userAgreements$: Observable<UserAgreementManagement[]>;
  private readonly userAgreementsCollectionPath = USER_AGREEMENTS_MANAGEMENT_FIRESTORE_COLLECTION;
  private agentSearchService = inject(AgentSearchService);

  subversionsDataSource$: Observable<DataSource>;
  agreementsInDraft$: Observable<UserAgreementManagement[]>;
  private reloadTrigger$ = new BehaviorSubject<void>(undefined);

  private _currentUserAgreementsSig = signal<CurrentUserAgreements>(null);
  currentUserAgreements$ = toObservable(this._currentUserAgreementsSig).pipe(shareReplay(1));
  /**
   * @deprecated Use currentUserAgreements$ instead to cache data
   */
  currentUserAgreementsSig = toSignal(this.currentUserAgreements$); // remove after portal migrate to currentUserAgreements$

  private _fileVersionsSig = signal<string[]>([]);
  readonly fileVersionsSig = this._fileVersionsSig.asReadonly();

  publishedPrivacyPolicySig = toSignal(
    this.getCurrentPublishedDocumentByType(PolicyTypes.privacyPolicy, SupportedLanguages.en),
  );

  publishedTermsOfServiceSig = toSignal(
    this.getCurrentPublishedDocumentByType(PolicyTypes.termsOfService, SupportedLanguages.en),
  );

  publishedUserAgreementsSig = computed(() => {
    const publishedPrivacyPolicy: UserAgreementManagement[] = this.publishedPrivacyPolicySig();
    const publishedTermsOfService: UserAgreementManagement[] = this.publishedTermsOfServiceSig();
    return [...publishedPrivacyPolicy, ...publishedTermsOfService];
  });

  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    super(fireBaseApp, UserAgreementsManagementService.fromFirestore, UserAgreementsManagementService.toFirestore);
    super.setCollection(this.userAgreementsCollectionPath);

    this._userAgreements$ = this.reloadTrigger$.pipe(
      switchMap(() =>
        this.getList([], { sortField: UserAgreementManagementKeys.fileVersion }).pipe(
          distinctUntilChanged((prev, curr) => {
            if (prev === curr) {
              return true;
            }
            if (!Array.isArray(prev) || !Array.isArray(curr)) {
              return false;
            }
            if (prev.length !== curr.length) {
              return false;
            }
            // compare by last updated date and dbIds set
            const byId = (a: UserAgreementManagement[]) => new Set(a.map(i => i?.[BaseModelKeys.dbId] ?? ''));
            const prevIds = byId(prev);
            for (const id of byId(curr)) {
              if (!prevIds.has(id)) {
                return false;
              }
            }
            const lastUpdated = (arr: UserAgreementManagement[]) =>
              arr.reduce<number>((acc, i) => Math.max(acc, (i?.[BaseModelKeys.updatedDate] as unknown as number) || 0), 0);
            return lastUpdated(prev) === lastUpdated(curr);
          }),
          shareReplay(1),
        ),
      ),
    );

    this.subversionsDataSource$ = this._userAgreements$.pipe(
      switchMap(agreements => {
        if (!agreements?.length) {
          return of(new DataSource({
            store: new ArrayStore({
              key: BaseModelKeys.dbId,
              data: [],
            }),
          }));
        }

        return updateWithAgentMetadata(agreements, this.agentSearchService).pipe(
          tap(data => {
            const uniqueFileVersions = Array.from(new Set((data || []).map(file => file.fileVersion).filter(Boolean)));
            this._fileVersionsSig.set(cloneDeep(uniqueFileVersions));

            const currentUserAgreements = { ...initialCurrentUserAgreements };
            (data || []).forEach(item => {
              if (item?.[UserAgreementManagementKeys.agreementStatus] === UserAgreementStatus.published) {
                const agreementType = item?.[UserAgreementManagementKeys.agreementType];
                currentUserAgreements[agreementType] = item?.[UserAgreementManagementKeys.fileVersion] ?? null;
              }
            });
            this._currentUserAgreementsSig.set(cloneDeep(currentUserAgreements));
          }),
          map(
            data =>
              new DataSource({
                store: new ArrayStore({
                  key: BaseModelKeys.dbId,
                  data: Array.isArray(data) ? data : [],
                }),
              }),
          ),
        );
      }),
      shareReplay(1),
    );

    this.agreementsInDraft$ = this._userAgreements$.pipe(
      map(agreements => agreements.filter(agreement => agreement.agreementStatus === UserAgreementStatus.draft)),
      shareReplay(1),
    );
  }

  static readonly fromFirestore = userAgreementFromFirestore;

  static readonly toFirestore = (data): UserAgreementManagement => {
    Object.assign(data, { [BaseModelKeys.actionName]: ChangeSourceType.regularArm });
    return data;
  };

  getCurrentPublishedDocumentByType(
    type: PolicyTypes,
    language: SupportedLanguages,
  ): Observable<UserAgreementManagement[]> {
    const qp: QueryParam[] = [
      new QueryParam(UserAgreementManagementKeys.agreementType, WhereFilterOperandKeys.equal, type),
      new QueryParam(UserAgreementManagementKeys.language, WhereFilterOperandKeys.equal, language),
      new QueryParam(
        UserAgreementManagementKeys.agreementStatus,
        WhereFilterOperandKeys.equal,
        UserAgreementStatus.published,
      ),
    ];

    return this.fsDao.getList(this.userAgreementsCollectionPath, qp, null, false);
  }

  getPublishedSubversionsByLanguagesMap(subversions: string[]): Observable<Partial<Record<SupportedLanguages, UserAgreementManagement>>> {
    return this.getSubversionByDbIds(subversions).pipe(
      map((agreements: UserAgreementManagement[]) => {
        const mapResult: Partial<Record<SupportedLanguages, UserAgreementManagement>> = {};
        for (const agreement of agreements) {
          const lang = agreement.language;
          if (lang && Object.values(SupportedLanguages).includes(lang)) {
            mapResult[lang] = agreement;
          }
        }
        return mapResult;
      }),
    );
  }

  getSubversionByDbIds(dbIds: string[], qp: QueryParam[] = []): Observable<UserAgreementManagement[]> {
    qp.push(new QueryParam(BaseModelKeys.dbId, WhereFilterOperandKeys.in, dbIds));
    return this.fsDao.getList(this.userAgreementsCollectionPath, qp, {
      sortField: UserAgreementManagementKeys.language,
      order: 'asc',
    }, false);
  }

  getLanguageSubversions(type: PolicyTypes, language: SupportedLanguages, statuses: UserAgreementStatus[] = []) {
    const qp: QueryParam[] = [];
    qp.push(new QueryParam(UserAgreementManagementKeys.agreementType, WhereFilterOperandKeys.equal, type));
    qp.push(new QueryParam(UserAgreementManagementKeys.language, WhereFilterOperandKeys.equal, language));
    if (!!statuses?.length) {
      qp.push(new QueryParam(UserAgreementManagementKeys.agreementStatus, WhereFilterOperandKeys.in, statuses));
    }
    return this.fsDao.getList(
      this.userAgreementsCollectionPath,
      qp,
      {
        sortField: BaseModelKeys.createdDate,
        order: 'desc',
      },
      false,
    );
  }

  reloadSubversionsDataSource(): void {
    this.reloadTrigger$.next();
  }
}
